import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatEuro, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";

const EMPTY_LINE = { designation: "", description: "", quantite: 1, unite: "u", prix_unitaire: 0, tva: 0 };
const newLine = () => ({ ...EMPTY_LINE, _uid: `l-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` });

export default function FactureNew() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: "",
    titre: "",
    objet: "",
    lignes: [newLine()],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const totals = (() => {
    const ht = form.lignes.reduce((s, l) => s + Number(l.quantite || 0) * Number(l.prix_unitaire || 0), 0);
    const tva = form.lignes.reduce(
      (s, l) => s + (Number(l.quantite || 0) * Number(l.prix_unitaire || 0) * Number(l.tva || 0)) / 100,
      0,
    );
    return { ht, tva, ttc: ht + tva };
  })();

  const setLine = (i, k, v) =>
    setForm((s) => {
      const lignes = [...s.lignes];
      lignes[i] = { ...lignes[i], [k]: v };
      return { ...s, lignes };
    });
  const addLine = () => setForm((s) => ({ ...s, lignes: [...s.lignes, newLine()] }));
  const removeLine = (i) =>
    setForm((s) => ({
      ...s,
      lignes: s.lignes.length > 1 ? s.lignes.filter((_, idx) => idx !== i) : s.lignes,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Sélectionne un client");
      return;
    }
    if (!form.titre.trim()) {
      toast.error("Donne un titre à la facture");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: "directe",
        client_id: form.client_id,
        titre: form.titre.trim(),
        objet: form.objet.trim() || undefined,
        lignes: form.lignes.map(({ _uid, ...l }) => ({
          ...l,
          quantite: Number(l.quantite),
          prix_unitaire: Number(l.prix_unitaire),
          tva: Number(l.tva),
        })),
      };
      const { data } = await api.post("/admin/factures", payload);
      toast.success(`Facture ${data.numero} créée`);
      navigate(`/admin/factures/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="facture-new-page">
      <Link to="/admin/factures" className="text-eyebrow inline-flex items-center gap-2 link-underline-static mb-6">
        <ArrowLeft size={14} /> Retour aux factures
      </Link>
      <div className="mb-8">
        <p className="text-eyebrow mb-3">Nouvelle facture</p>
        <h1 className="h-display text-4xl md:text-5xl">Facture directe.</h1>
        <p className="text-muted-foreground text-[13.5px] mt-3 max-w-xl">
          Sans devis préalable. Pour les petits chantiers, dépannages ou réparations facturés sur place.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-8 max-w-4xl">
        {/* Client + titre */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-eyebrow block mb-2">Client *</label>
            <select
              value={form.client_id}
              onChange={(e) => setForm((s) => ({ ...s, client_id: e.target.value }))}
              className="w-full bg-transparent border-b border-border py-2 text-[14px] outline-none"
              data-testid="facture-new-client"
              required
            >
              <option value="">— Sélectionne un client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom} {c.type === "cabinet_expertise" ? "· Cabinet" : c.type === "professionnel" ? "· Pro" : ""}
                </option>
              ))}
            </select>
            <Link to="/admin/clients" className="text-[11px] text-primary link-underline-static mt-2 inline-block">
              + Créer un nouveau client
            </Link>
          </div>
          <div>
            <label className="text-eyebrow block mb-2">Titre de la facture *</label>
            <input
              type="text"
              value={form.titre}
              onChange={(e) => setForm((s) => ({ ...s, titre: e.target.value }))}
              placeholder="Ex. Réparation porte palière"
              className="w-full bg-transparent border-b border-border py-2 text-[14px] outline-none"
              data-testid="facture-new-titre"
              required
            />
          </div>
        </div>

        <div>
          <label className="text-eyebrow block mb-2">Objet (optionnel)</label>
          <input
            type="text"
            value={form.objet}
            onChange={(e) => setForm((s) => ({ ...s, objet: e.target.value }))}
            placeholder="Détail rapide affiché sous le titre de la facture"
            className="w-full bg-transparent border-b border-border py-2 text-[14px] outline-none"
            data-testid="facture-new-objet"
          />
        </div>

        {/* Lignes */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-eyebrow">Lignes</label>
            <button
              type="button"
              onClick={addLine}
              className="text-eyebrow text-primary link-underline-static"
              data-testid="facture-new-add-line"
            >
              <Plus size={12} className="inline mr-1" /> Ajouter une ligne
            </button>
          </div>
          <div className="space-y-3">
            {form.lignes.map((l, i) => (
              <div
                key={l._uid || `line-${i}`}
                className="border border-border p-4 grid md:grid-cols-12 gap-3"
                data-testid={`facture-new-line-${i}`}
              >
                <div className="md:col-span-4">
                  <input
                    placeholder="Désignation"
                    value={l.designation}
                    onChange={(e) => setLine(i, "designation", e.target.value)}
                    className="w-full bg-transparent border-b border-border py-1.5 text-[13px] outline-none"
                  />
                  <input
                    placeholder="Description (optionnelle)"
                    value={l.description}
                    onChange={(e) => setLine(i, "description", e.target.value)}
                    className="w-full bg-transparent border-b border-border py-1.5 text-[12.5px] outline-none mt-2 text-muted-foreground"
                  />
                </div>
                <div className="md:col-span-1">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Qté"
                    value={l.quantite}
                    onChange={(e) => setLine(i, "quantite", e.target.value)}
                    className="w-full bg-transparent border-b border-border py-1.5 text-[13px] outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <input
                    placeholder="Unité"
                    value={l.unite}
                    onChange={(e) => setLine(i, "unite", e.target.value)}
                    className="w-full bg-transparent border-b border-border py-1.5 text-[13px] outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="P.U. HT"
                    value={l.prix_unitaire}
                    onChange={(e) => setLine(i, "prix_unitaire", e.target.value)}
                    className="w-full bg-transparent border-b border-border py-1.5 text-[13px] outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <div
                    className="w-full border-b border-border py-1.5 text-[13px] text-right tabular-nums text-primary font-medium"
                    data-testid={`facture-new-line-montant-ht-${i}`}
                  >
                    {formatEuro(Number(l.quantite || 0) * Number(l.prix_unitaire || 0))}
                  </div>
                </div>
                <div className="md:col-span-1 flex items-start justify-end">
                  {form.lignes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      className="text-muted-foreground hover:text-destructive p-1"
                      aria-label="Supprimer la ligne"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Total — auto-entrepreneur, pas de TVA */}
        <div className="border-t border-border pt-6 flex justify-end">
          <div className="text-right space-y-1.5">
            <p className="text-serial">Net à payer</p>
            <p className="font-serif text-3xl text-primary">{formatEuro(totals.ht)}</p>
            <p className="text-[10px] text-muted-foreground mt-2 italic">
              TVA non applicable, art. 293 B du CGI
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Link to="/admin/factures" className="px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-medium border border-border hover:bg-muted">
            Annuler
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="bg-foreground text-background px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-primary transition-colors disabled:opacity-50"
            data-testid="facture-new-submit"
          >
            {saving ? "Création…" : "Créer la facture"}
          </button>
        </div>
      </form>
    </div>
  );
}
